import React, { useState, useRef, useEffect } from "react";
import "./Sidebar.css";

const SearchIcon = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none"
    style={{ flexShrink: 0, marginRight: 7, opacity: 0.7 }}>
    <rect x="1" y="1" width="12" height="12" rx="2.5"
      stroke="currentColor" strokeWidth="1.4" />
    <path d="M4 4.5h6M4 7h6M4 9.5h3.5"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const ChatIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"
    style={{ flexShrink: 0, opacity: 0.7 }}>
    <path d="M1 2.5C1 1.67 1.67 1 2.5 1h7C10.33 1 11 1.67 11 2.5v5c0 .83-.67 1.5-1.5 1.5H7L4.5 11V9H2.5C1.67 9 1 8.33 1 7.5v-5z"
      stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

export default function Sidebar({
  sidebarOpen, setSidebarOpen,
  user, plan, leadsUsed, leadsLimit, isAtLimit, loading,
  searches, activeSearchId, activeChatId,
  onLoadSearch, onLoadAIChat, onUpgrade,
  onDeleteSearch, onRenameSearch, showAuthModal,
  onNewSearch,
}) {
  const [expandedSearch,  setExpandedSearch]  = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [menuOpenId,      setMenuOpenId]      = useState(null);
  const [renamingId,      setRenamingId]      = useState(null);
  const [renameValue,     setRenameValue]     = useState('');

  const profileRef = useRef(null);
  const renameRef  = useRef(null);

  useEffect(() => {
    const h = e => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = () => setMenuOpenId(null);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (renamingId && renameRef.current) { renameRef.current.focus(); renameRef.current.select(); }
  }, [renamingId]);

  useEffect(() => {
    if (activeSearchId) setExpandedSearch(activeSearchId);
  }, [activeSearchId]);

  const handleLoadSearch = (searchId) => {
    if (expandedSearch === searchId) {
      setExpandedSearch(null);
    } else {
      setExpandedSearch(searchId);
      onLoadSearch(searchId);
    }
  };

  const handleDotsClick   = (e, searchId) => { e.stopPropagation(); setMenuOpenId(prev => prev === searchId ? null : searchId); };
  const handleRenameClick = (e, s)         => { e.stopPropagation(); setMenuOpenId(null); setRenamingId(s.id); setRenameValue(s.label); };
  const commitRename      = (searchId)     => { if (renameValue.trim() && onRenameSearch) onRenameSearch(searchId, renameValue.trim()); setRenamingId(null); setRenameValue(''); };
  const cancelRename      = ()             => { setRenamingId(null); setRenameValue(''); };
  const handleDeleteClick = (e, searchId) => { e.stopPropagation(); setMenuOpenId(null); if (onDeleteSearch) onDeleteSearch(searchId); };

  const SW = sidebarOpen ? 240 : 48;

  return (
    <div className="gr-sidebar" style={{ width: SW }}>

      {/* Top */}
      <div className="gr-sidebar-top" style={{ justifyContent: sidebarOpen ? "space-between" : "center" }}>
        {sidebarOpen && (
          <div className="gr-logo">
            <img src="/headerlogo.png" alt="NYLeads" style={{ width: 22, height: 22, borderRadius: 4, flexShrink: 0 }} />
            <span className="gr-logo-text">NYLeads</span>
          </div>
        )}
        <button className="gr-toggle-btn" onClick={() => setSidebarOpen(v => !v)}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            {sidebarOpen
              ? <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              : <path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>}
          </svg>
        </button>
      </div>

      {/* New search */}
      <div className="gr-new-btn" onClick={onNewSearch} style={{ cursor: 'pointer' }}>
        <div className="gr-new-btn-icon">{user ? '' : ''}</div>
        {sidebarOpen && <span>{loading ? "Fetching..." : user ? "New Search" : "Sign In"}</span>}
      </div>

      {/* Recent searches */}
      {sidebarOpen && searches.length > 0 && (
        <>
          <div className="gr-section-label">Recent</div>
          {searches.map(s => (
            <div key={s.id} className="gr-search-group">

              {/* Search row */}
              <div
                className={`gr-side-search-row${activeSearchId === s.id && !activeChatId ? ' active' : ''}`}
                onClick={() => renamingId !== s.id && handleLoadSearch(s.id)}
              >
                <SearchIcon />

                {/* Rename input */}
                {renamingId === s.id ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                    <input ref={renameRef} value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') commitRename(s.id); if (e.key === 'Escape') cancelRename(); }}
                      style={{ flex: 1, minWidth: 0, background: '#181818', border: '1px solid #444',
                        borderRadius: 4, padding: '3px 6px', color: '#f0f0f0', fontSize: 12,
                        fontFamily: 'DM Sans, sans-serif', outline: 'none' }} />
                    <button onMouseDown={e => { e.stopPropagation(); commitRename(s.id); }}
                      style={{ background: '#e55f00', border: 'none', borderRadius: 4, color: '#fff',
                        fontSize: 10, fontWeight: 700, padding: '3px 7px', cursor: 'pointer',
                        flexShrink: 0, fontFamily: 'DM Sans, sans-serif' }}>Save</button>
                    <button onMouseDown={e => { e.stopPropagation(); cancelRename(); }}
                      style={{ background: 'none', border: '1px solid #333', borderRadius: 4,
                        color: '#aaa', fontSize: 10, padding: '3px 6px', cursor: 'pointer',
                        flexShrink: 0, fontFamily: 'DM Sans, sans-serif' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: '#f0f0f0',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'DM Sans, sans-serif' }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaaaaa', fontFamily: 'IBM Plex Mono, monospace', marginTop: 1 }}>
                      {s.count} leads · {(s.aiChats || []).length} AI chats
                    </div>
                  </div>
                )}

                {/* Three-dot menu */}
                {renamingId !== s.id && (
                  <div style={{ position: 'relative', flexShrink: 0 }} onMouseDown={e => e.stopPropagation()}>
                    <button onClick={e => handleDotsClick(e, s.id)}
                      style={{ background: menuOpenId === s.id ? '#252525' : 'transparent', border: 'none',
                        cursor: 'pointer', color: '#bbbbbb', width: 22, height: 22, borderRadius: 4,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, letterSpacing: '1px', padding: 0, fontFamily: 'monospace' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#252525'; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = menuOpenId === s.id ? '#252525' : 'transparent'; e.currentTarget.style.color = menuOpenId === s.id ? '#fff' : '#bbbbbb'; }}>
                      ···
                    </button>
                    {menuOpenId === s.id && (
                      <div onMouseDown={e => e.stopPropagation()}
                        style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#1c1c1c',
                          border: '1px solid #333', borderRadius: 8, overflow: 'hidden', minWidth: 120,
                          zIndex: 300, boxShadow: '0 8px 24px rgba(0,0,0,0.8)' }}>
                        <div onClick={e => handleRenameClick(e, s)}
                          style={{ padding: '9px 12px', fontSize: 12.5, fontFamily: 'DM Sans, sans-serif',
                            color: '#f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center',
                            gap: 8, borderBottom: '1px solid #2a2a2a' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#252525'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          ✏️ Rename
                        </div>
                        <div onClick={e => handleDeleteClick(e, s.id)}
                          style={{ padding: '9px 12px', fontSize: 12.5, fontFamily: 'DM Sans, sans-serif',
                            color: '#ff5555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          🗑️ Delete
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Chevron */}
                {renamingId !== s.id && (
                  <svg width="8" height="8" viewBox="0 0 12 12" fill="none"
                    style={{ flexShrink: 0, marginLeft: 4, color: '#aaaaaa',
                      transform: expandedSearch === s.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>

              {/* AI chat branches */}
              {expandedSearch === s.id && (
                <div className="gr-ai-branches">
                  {(s.aiChats || []).length === 0 && (
                    <div style={{ paddingLeft: 28, paddingTop: 6, paddingBottom: 6,
                      fontSize: 10, color: '#aaaaaa', fontFamily: 'IBM Plex Mono, monospace' }}>
                      No AI chats yet
                    </div>
                  )}
                  {(s.aiChats || []).map(chat => (
                    <div key={chat.id}
                      className={`gr-ai-branch${activeChatId === chat.id ? ' active' : ''}`}
                      onClick={() => onLoadAIChat(s.id, chat.id)}>
                      <div className="gr-ai-branch-line" />
                      <div className="gr-ai-branch-content">
                        <ChatIcon />
                        <div style={{ overflow: 'hidden', flex: 1 }}>
                          <div className="gr-ai-branch-label">
                            {chat.query.length > 28 ? chat.query.substring(0, 28) + '…' : chat.query}
                          </div>
                          <div className="gr-ai-branch-meta">{chat.mode}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          ))}
        </>
      )}

      {/* Footer */}
      <div className="gr-sidebar-footer">
        {sidebarOpen && user && (
          <div className="gr-usage-bar">
            <div className="gr-usage-top">
              <span className="gr-usage-label">{leadsUsed} / {leadsLimit} leads</span>
              <span className="gr-usage-plan">{plan}</span>
            </div>
            <div className="gr-usage-track">
              <div className="gr-usage-fill" style={{
                width: `${Math.min((leadsUsed / leadsLimit) * 100, 100)}%`,
                background: isAtLimit ? '#ef4444' : '#e55f00',
              }} />
            </div>
            {isAtLimit && <button className="gr-usage-upgrade" onClick={onUpgrade}>Upgrade plan ↗</button>}
          </div>
        )}
        <div className="gr-profile-wrap" ref={profileRef}>
          {profileMenuOpen && user && (
            <div className="gr-profile-menu">
              <div className="gr-profile-menu-user">
                <div className="gr-profile-menu-name">{user.displayName || 'Account'}</div>
                <div className="gr-profile-menu-email">{user.email}</div>
              </div>
              <div className="gr-profile-menu-divider" />
              <button className="gr-profile-menu-item" onClick={() => { onUpgrade(); setProfileMenuOpen(false); }}>
                <span className="gr-profile-menu-icon">⚡</span><span>Upgrade Plan</span>
                <span className="gr-profile-menu-badge">{plan}</span>
              </button>
              <div className="gr-profile-menu-divider" />
              <button className="gr-profile-menu-item gr-profile-menu-danger"
                onClick={async () => {
                  const { signOut } = await import('firebase/auth');
                  const { auth }    = await import('./firebase');
                  await signOut(auth);
                  setProfileMenuOpen(false);
                }}>
                <span className="gr-profile-menu-icon">→</span><span>Sign Out</span>
              </button>
            </div>
          )}
          <div className="gr-user-row" onClick={() => user ? setProfileMenuOpen(v => !v) : showAuthModal('login')}>
            <div className="gr-avatar">
              {user ? (user.displayName?.[0] || user.email?.[0] || 'U').toUpperCase() : '?'}
            </div>
            {sidebarOpen && (
              <div style={{ overflow: "hidden", flex: 1 }}>
                <div className="gr-user-name">{user ? (user.displayName || 'Account') : 'Sign In'}</div>
                <div className="gr-user-email">{user ? user.email : 'Click to sign in'}</div>
              </div>
            )}
            {sidebarOpen && user && (
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"
                style={{ flexShrink: 0, color: '#888', transform: profileMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}